"use client";

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import { apiPost, apiGet } from "../apiClient";
import type { User, LoginResponse, RegisterRequest, AuthContextType } from "./types";
import { normalizeRole, normalizeUser, toBackendRegisterPayload } from "./types";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();

  // Load user from token on mount
  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        // Try to fetch current user based on their role
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const raw = JSON.parse(storedUser);
          // Normalize role — handles FACILITY_USER → "facility", NURSE → "nurse", etc.
          const parsedUser: User = {
            ...raw,
            role: normalizeRole(raw.role ?? ""),
          };
          setUser(parsedUser);

          // Verify token is still valid by making an API call
          try {
            if (parsedUser.role === "nurse") {
              await apiGet("/nurses/me");
            } else if (parsedUser.role === "facility") {
              await apiGet("/facilities/me");
            }
          } catch (_error) {
            // Token is invalid, clear auth state
            localStorage.removeItem("access_token");
            localStorage.removeItem("user");
            setUser(null);
          }
        }
      } catch (error) {
        console.error("Failed to load user:", error);
        localStorage.removeItem("access_token");
        localStorage.removeItem("user");
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();
  }, []);

  const login = useCallback(async (email: string, password: string, rememberMe = false) => {
    try {
      const response = await apiPost<LoginResponse>("/auth/login", {
        email,
        password,
      });

      const user = normalizeUser(response.user);

      // Store access token and user info (refresh token is in httpOnly cookie)
      localStorage.setItem("access_token", response.access_token);
      localStorage.setItem("user", JSON.stringify(user));

      if (rememberMe) {
        localStorage.setItem("remember_me", "true");
      }

      setUser(user);

      // Redirect based on role
      if (user.role === "nurse") {
        router.push("/jobs");
      } else if (user.role === "facility") {
        router.push("/facility-dashboard");
      } else {
        router.push("/");
      }
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  }, [router]);

  const register = useCallback(async (data: RegisterRequest) => {
    try {
      const payload = toBackendRegisterPayload(data);
      const response = await apiPost<LoginResponse>("/auth/register", payload);

      const user = normalizeUser(response.user);

      // Store access token and user info (refresh token is in httpOnly cookie)
      localStorage.setItem("access_token", response.access_token);
      localStorage.setItem("user", JSON.stringify(user));

      setUser(user);

      // Redirect based on role
      if (user.role === "nurse") {
        router.push("/jobs");
      } else if (user.role === "facility") {
        router.push("/facility-dashboard");
      } else {
        router.push("/");
      }
    } catch (error) {
      console.error("Registration failed:", error);
      throw error;
    }
  }, [router]);

  const logout = useCallback(async () => {
    try {
      await apiPost("/auth/logout");
    } catch (_error) {
      // Backend logout may fail if token is already expired; proceed with local cleanup
    } finally {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user");
      localStorage.removeItem("remember_me");
      setUser(null);
      router.push("/login");
    }
  }, [router]);

  const refreshToken = useCallback(async () => {
    try {
      // Refresh token is sent via httpOnly cookie automatically
      const response = await apiPost<{ access_token: string }>("/auth/refresh");

      localStorage.setItem("access_token", response.access_token);
    } catch (error) {
      console.error("Token refresh failed:", error);
      logout();
      throw error;
    }
  }, [logout]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
